import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

function fmtInr(n) {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function buildDoc(data) {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid report data");
  }

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  doc.setFontSize(16);
  doc.setTextColor(17, 24, 39);
  let y = 16;
  doc.text("Portfolio report", 14, y);
  y += 7;

  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.text(`Folder: ${data.rootName || "—"}`, 14, y);
  y += 5;
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, y);
  y += 5;
  if (data.dateFrom || data.dateTo) {
    doc.text(
      `Period: ${data.dateFrom || "—"} → ${data.dateTo || "—"}`,
      14,
      y
    );
    y += 5;
  }

  let tableStartY = y + 3;
  if (data.truncated) {
    doc.setTextColor(180, 83, 9);
    doc.text(
      `Includes the latest ${data.transactionCount ?? 0} transactions (server limit ${data.maxTransactions ?? "—"}).`,
      14,
      tableStartY
    );
    doc.setTextColor(55, 65, 81);
    tableStartY += 6;
  }

  if (data.mode === "summary" && Array.isArray(data.summary)) {
    autoTable(doc, {
      startY: tableStartY,
      head: [["Folder", "Cash in (₹)", "Cash out (₹)", "Net (₹)", "Row count"]],
      body: data.summary.map((s) => [
        String(s.folder ?? "—"),
        fmtInr(s.cashIn),
        fmtInr(s.cashOut),
        fmtInr(s.net),
        String(s.transactions ?? 0),
      ]),
      headStyles: {
        fillColor: [15, 118, 110],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: { fontSize: 8, cellPadding: 1.5, valign: "middle" },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right", cellWidth: 22 },
      },
      margin: { left: 14, right: 14 },
    });
  } else if (data.mode === "detail" && Array.isArray(data.details)) {
    let cursorY = tableStartY;

    for (const section of data.details) {
      if (cursorY > 250) {
        doc.addPage();
        cursorY = 16;
      }

      doc.setFontSize(10);
      doc.setTextColor(15, 118, 110);
      doc.text(String(section.folder ?? "Folder"), 14, cursorY);
      doc.setTextColor(55, 65, 81);
      cursorY += 6;

      const rows = Array.isArray(section.rows) ? section.rows : [];
      const body =
        rows.length > 0
          ? rows.map((r) => [
              String(r.date ?? "—"),
              String(r.description ?? "—"),
              fmtInr(r.cashIn),
              fmtInr(r.cashOut),
            ])
          : [["—", "No transactions in this folder", "—", "—"]];

      autoTable(doc, {
        startY: cursorY,
        head: [["Date", "Description", "Cash in (₹)", "Cash out (₹)"]],
        body,
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: 255,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 7,
          cellPadding: 1.2,
          overflow: "linebreak",
          valign: "top",
        },
        columnStyles: {
          0: { cellWidth: 24 },
          1: { cellWidth: 78 },
          2: { halign: "right", cellWidth: 30 },
          3: { halign: "right", cellWidth: 30 },
        },
        margin: { left: 14, right: 14 },
      });

      cursorY = doc.lastAutoTable.finalY + 10;
    }
  }

  return doc;
}

/**
 * @returns {{ blob: Blob, fileName: string }}
 */
export function buildPortfolioAnalyticsPdfBlob(data, fileBaseName = "portfolio-report") {
  const doc = buildDoc(data);
  const safe = String(fileBaseName)
    .replace(/[^\w-]+/g, "-")
    .slice(0, 80);
  const name = `${safe || "portfolio-report"}.pdf`;
  const blob = doc.output("blob");
  return { blob, fileName: name };
}

export function downloadPortfolioAnalyticsPdf(data, fileBaseName = "portfolio-report") {
  const { blob, fileName } = buildPortfolioAnalyticsPdfBlob(data, fileBaseName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { blob, fileName };
}
